import * as Sentry from '@sentry/react';
import { VideoCaptureLifecycle, type CaptureVideoOptions, type PreparedVideoCapture } from './capture/VideoCaptureLifecycle';
export type { CaptureVideoOptions, PreparedVideoCapture, RetryableCapturePreparationError } from './capture/VideoCaptureLifecycle';

const DIAG = '[AUDIO-BUG-12009:TrackCapture]';

function classifyMediaError(error: unknown): string {
  if (error instanceof DOMException) return error.name;
  if (error instanceof Error) return error.constructor.name;
  return 'Unknown';
}

function trackSnapshot(track: MediaStreamTrack): Record<string, unknown> {
  const settings = track.getSettings();
  return {
    label: track.label,
    readyState: track.readyState,
    enabled: track.enabled,
    muted: track.muted,
    sampleRate: settings.sampleRate,
    channelCount: settings.channelCount,
    deviceId: settings.deviceId,
  };
}

/**
 * Web Audio pipeline that wraps a captured MediaStreamTrack:
 *
 *   source track → MediaStreamAudioSourceNode
 *                  ↓
 *                  GainNode  (user-controlled volume)
 *                  ↓
 *                  MediaStreamAudioDestinationNode → output track
 *
 * Why this exists:
 *  1. WebRTC's audio processing module (AGC/NS/EC/HPF) is only applied to
 *     tracks that come directly from getUserMedia. Once a track passes
 *     through a Web Audio graph and exits via MediaStreamAudioDestinationNode,
 *     APM is bypassed — capture stays bit-exact, no auto-gain.
 *  2. The GainNode is a real, mutable volume control. The legacy slider
 *     never actually controlled the audio because nothing in the active
 *     code path read its value.
 */
class AudioGainPipeline {
  public readonly outputTrack: MediaStreamTrack;
  private readonly context: AudioContext;
  private readonly source: MediaStreamAudioSourceNode;
  private readonly gain: GainNode;
  private readonly destination: MediaStreamAudioDestinationNode;
  private readonly sourceTrack: MediaStreamTrack;

  constructor(sourceTrack: MediaStreamTrack, initialGain: number) {
    this.sourceTrack = sourceTrack;

    // Match the AudioContext's sample rate to the captured track when known.
    // A mismatch forces Chrome to resample, and the resampler can produce
    // audible artifacts at small buffer sizes. WebRTC capture is typically
    // 48 kHz; we use that as a sane fallback when getSettings() doesn't say.
    const trackSampleRate = sourceTrack.getSettings().sampleRate;
    const targetSampleRate = typeof trackSampleRate === 'number' && trackSampleRate > 0
      ? trackSampleRate
      : 48000;

    // 'playback' uses larger buffers than 'interactive', dramatically reducing
    // the chance of crackle from underruns. Latency cost (~50-100ms extra) is
    // immaterial for a WebRTC stream that's already buffered downstream.
    try {
      this.context = new AudioContext({ latencyHint: 'playback', sampleRate: targetSampleRate });
    } catch {
      // Some platforms reject explicit sampleRate; fall back without it.
      this.context = new AudioContext({ latencyHint: 'playback' });
    }
    this.context.resume().catch(() => undefined);

    this.source = this.context.createMediaStreamSource(new MediaStream([sourceTrack]));
    this.gain = this.context.createGain();
    this.gain.gain.value = clampGain(initialGain);
    // Match channel count to source where possible so we don't unnecessarily
    // upmix a stereo capture to mono or vice versa.
    const trackChannelCount = sourceTrack.getSettings().channelCount;
    if (typeof trackChannelCount === 'number' && trackChannelCount > 0) {
      this.destination = this.context.createMediaStreamDestination();
      try {
        this.destination.channelCount = trackChannelCount;
        this.destination.channelCountMode = 'explicit';
        this.destination.channelInterpretation = 'speakers';
      } catch {
        // Some Chromium builds reject channelCount changes on the destination;
        // safe to ignore — default channel mixing will still produce audio.
      }
    } else {
      this.destination = this.context.createMediaStreamDestination();
    }

    this.source.connect(this.gain);
    this.gain.connect(this.destination);

    const out = this.destination.stream.getAudioTracks()[0];
    if (!out) {
      this.destroy();
      throw new Error('AudioGainPipeline: destination produced no audio track');
    }
    this.outputTrack = out;
  }

  public setGain(gain: number): void {
    const clamped = clampGain(gain);
    // Short ramp avoids zipper noise on rapid slider drags.
    const now = this.context.currentTime;
    this.gain.gain.cancelScheduledValues(now);
    this.gain.gain.setTargetAtTime(clamped, now, 0.02);
  }

  public destroy(): void {
    try { this.source.disconnect(); } catch { /* may be detached */ }
    try { this.gain.disconnect(); } catch { /* may be detached */ }
    try { this.destination.disconnect(); } catch { /* may be detached */ }
    try { this.outputTrack.stop(); } catch { /* may already be stopped */ }
    try { this.context.close(); } catch { /* may already be closed */ }
    try { this.sourceTrack.stop(); } catch { /* may already be stopped */ }
  }
}

function clampGain(g: number): number {
  if (!Number.isFinite(g)) return 1;
  if (g < 0) return 0;
  if (g > 4) return 4; // upper bound prevents catastrophic boost from a bad caller
  return g;
}

/**
 * TrackCaptureManager - Handles getUserMedia calls and track lifecycle.
 *
 * Responsibilities:
 * - Capture video and audio tracks from devices
 * - Monitor track health (ended, muted states)
 * - Re-initialize tracks when devices change
 * - Provide current active tracks
 */
export class TrackCaptureManager {
  private readonly video = new VideoCaptureLifecycle({
    acquire: constraints => navigator.mediaDevices.getUserMedia({ video: constraints }),
    changed: track => {
      if (track) this.attachTrackHealthMonitor(track, 'video');
      this.notifyTrackChanged();
    },
  });
  /**
   * The track exposed to consumers (WebRTC sender). When a gain pipeline is
   * active this is the pipeline's output track. When no pipeline is active
   * (e.g. capture failed) this falls back to null.
   */
  private audioTrack: MediaStreamTrack | null = null;
  /**
   * The Web Audio pipeline applied to captured audio. Owns:
   *  - the AudioContext
   *  - the raw captured track (kept alive as long as the pipeline is alive)
   *  - the output track exposed as `audioTrack`
   */
  private audioPipeline: AudioGainPipeline | null = null;
  private currentGain = 1.0;
  private onTrackChanged: ((video: MediaStreamTrack | null, audio: MediaStreamTrack | null) => void) | null = null;

  public setOnTrackChanged(callback: (video: MediaStreamTrack | null, audio: MediaStreamTrack | null) => void): void {
    this.onTrackChanged = callback;
  }

  public getVideoTrack(): MediaStreamTrack | null {
    return this.video.getVideoTrack();
  }

  public getAudioTrack(): MediaStreamTrack | null {
    return this.audioTrack;
  }

  /**
   * Set the gain applied to all captured audio (game audio or desktop audio).
   * `gain` is a linear multiplier: 0 = silence, 1.0 = unity (no change).
   * Persists across capture restarts.
   */
  public setAudioGain(gain: number): void {
    this.currentGain = gain;
    if (this.audioPipeline) {
      this.audioPipeline.setGain(gain);
    }
  }

  public captureVideo(constraints: MediaTrackConstraints, options?: CaptureVideoOptions): Promise<MediaStreamTrack | null> {
    return this.video.captureVideo(constraints, options);
  }

  public prepareVideo(constraints: MediaTrackConstraints,
    options?: CaptureVideoOptions & { maxAcquisitionAttempts?: 1 | 2 }): Promise<PreparedVideoCapture | null> {
    return this.video.prepareVideo(constraints, options);
  }

  public commitPreparedVideo(prepared: PreparedVideoCapture, isCurrent?: () => boolean): MediaStreamTrack {
    return this.video.commitPreparedVideo(prepared, isCurrent);
  }

  public discardPreparedVideo(prepared: PreparedVideoCapture): void { this.video.discardPreparedVideo(prepared); }
  public retireVideoTrack(track: MediaStreamTrack): void { this.video.retireVideoTrack(track); }

  public async captureAudio(constraints: MediaTrackConstraints): Promise<MediaStreamTrack | null> {
    this.stopAudioTrack();

    try {
      console.log(`${DIAG} captureAudio called`, { constraints });
      const stream = await navigator.mediaDevices.getUserMedia({ audio: constraints });
      const track = stream.getAudioTracks()[0];
      if (!track) {
        console.warn(`${DIAG} getUserMedia returned stream with no audio tracks`);
        return null;
      }

      const snapshot = trackSnapshot(track);
      console.log(`${DIAG} Audio track captured successfully`, snapshot);
      Sentry.addBreadcrumb({
        category: 'AUDIO-BUG-12009',
        message: 'Audio track captured',
        level: 'info',
        data: snapshot,
      });

      const outputTrack = this.installAudioPipeline(track, 'capture');
      this.attachTrackHealthMonitor(track, 'audio');
      this.notifyTrackChanged();
      return outputTrack;
    } catch (error) {
      const errorType = classifyMediaError(error);
      console.error(`${DIAG} getUserMedia FAILED`, { errorType, error, constraints });
      Sentry.captureException(error, {
        tags: { audio_stage: 'capture', audio_error_type: errorType, audio_diag: 'AUDIO-BUG-12009' },
        contexts: { audioCapture: { constraints: JSON.stringify(constraints), errorType } },
      });
      return null;
    }
  }

  public async captureDesktopAudio(): Promise<MediaStreamTrack | null> {
    this.stopAudioTrack();

    try {
      console.log(`${DIAG} captureDesktopAudio called`);
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: false,
        audio: {
          suppressLocalAudioPlayback: false,
        } as MediaTrackConstraints,
      });
      const track = stream.getAudioTracks()[0];
      if (!track) {
        console.warn(`${DIAG} getDisplayMedia returned stream with no audio tracks`);
        return null;
      }

      const snapshot = trackSnapshot(track);
      console.log(`${DIAG} Desktop audio track captured successfully`, snapshot);
      Sentry.addBreadcrumb({
        category: 'AUDIO-BUG-12009',
        message: 'Desktop audio track captured',
        level: 'info',
        data: snapshot,
      });

      const outputTrack = this.installAudioPipeline(track, 'desktop_capture');
      this.attachTrackHealthMonitor(track, 'audio');
      this.notifyTrackChanged();
      return outputTrack;
    } catch (error) {
      const errorType = classifyMediaError(error);
      console.error(`${DIAG} getDisplayMedia FAILED`, { errorType, error });
      Sentry.captureException(error, {
        tags: { audio_stage: 'desktop_capture', audio_error_type: errorType, audio_diag: 'AUDIO-BUG-12009' },
        contexts: { audioCapture: { source: 'desktop', errorType } },
      });
      return null;
    }
  }

  public stopVideoTrack(): void { this.video.stopVideoTrack(); }

  public stopAudioTrack(): void {
    // Tearing down the pipeline stops both the synthesized output track and
    // the upstream capture track it owns; leave audioTrack pointing at null.
    if (this.audioPipeline) {
      this.audioPipeline.destroy();
      this.audioPipeline = null;
    }
    this.audioTrack = null;
  }

  public destroy(): void {
    this.stopVideoTrack();
    this.stopAudioTrack();
    this.onTrackChanged = null;
  }

  /**
   * Wraps a freshly captured raw audio track in an AudioGainPipeline and
   * exposes the pipeline's output track as this.audioTrack. On failure
   * (older browsers without Web Audio MediaStreamDestination support) falls
   * back to the raw track so capture still works — gain control simply won't
   * be available.
   */
  private installAudioPipeline(rawTrack: MediaStreamTrack, stage: 'capture' | 'desktop_capture'): MediaStreamTrack {
    try {
      const pipeline = new AudioGainPipeline(rawTrack, this.currentGain);
      this.audioPipeline = pipeline;
      this.audioTrack = pipeline.outputTrack;
      console.log(`${DIAG} Audio gain pipeline installed (initial gain=${this.currentGain})`);
      return pipeline.outputTrack;
    } catch (error) {
      console.warn(`${DIAG} Failed to install audio gain pipeline, using raw track`, error);
      Sentry.captureException(error, {
        tags: { audio_stage: stage, audio_diag: 'AUDIO-BUG-12009' },
        contexts: { audioPipeline: { reason: 'install_failed' } },
      });
      this.audioPipeline = null;
      this.audioTrack = rawTrack;
      return rawTrack;
    }
  }

  private attachTrackHealthMonitor(track: MediaStreamTrack, kind: 'video' | 'audio'): void {
    track.addEventListener('ended', () => {
      const snapshot = { label: track.label, readyState: track.readyState, muted: track.muted, enabled: track.enabled };
      console.warn(`${DIAG} ${kind} track ended unexpectedly`, snapshot);
      Sentry.addBreadcrumb({
        category: 'AUDIO-BUG-12009',
        message: `${kind} track ended`,
        level: 'warning',
        data: snapshot,
      });
      if (kind === 'video') {
        // A superseded track that we retired after a swap must not null out its replacement.
        this.video.ended(track);
        return;
      } else {
        this.audioTrack = null;
      }
      this.notifyTrackChanged();
    });

    track.addEventListener('mute', () => {
      console.warn(`${DIAG} ${kind} track muted (hardware stopped sending data)`, {
        label: track.label, readyState: track.readyState,
      });
      Sentry.addBreadcrumb({
        category: 'track-health',
        message: `${kind} track mute event fired`,
        level: 'warning',
        data: { kind, label: track.label, readyState: track.readyState },
      });
    });
    track.addEventListener('unmute', () => {
      console.log(`${DIAG} ${kind} track unmuted (hardware resumed)`, { label: track.label });
    });
  }

  private notifyTrackChanged(): void {
    this.onTrackChanged?.(this.video.getVideoTrack(), this.audioTrack);
  }
}
